'use client';

import { useMemo, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { getPersonalBalanceOnly } from '@/services/user';
import { useSession } from '@descope/nextjs-sdk/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserWatchers } from '@/services/watchers.service';
import { jwtDecode } from 'jwt-decode';
import { Permissions } from '@/lib/permissions';
import { useSocketContext } from '@/contexts/SocketContext';
import {
  TEAM_CREDITS_QUERY_KEY,
  TEAM_CREDITS_STALE_TIME_MS,
} from '@/components/teams/hooks/useTeamCredits';
import { getTeamCredits } from '@/services/teams-client.service';
import type { TeamCredits } from '@/components/teams/types';
import { getUserBootstrap } from '@/services/user-bootstrap';

export const useUserData = (options?: { loadBalance?: boolean }) => {
  const { sessionToken, isAuthenticated } = useSession();
  const queryClient = useQueryClient();
  const previousTeamIdRef = useRef<string | null | undefined>(undefined);
  const pathname = usePathname();

  // During the signup/login handoff the client is already authenticated while
  // the session cookie may not be usable server-side yet. Nothing on these
  // pages consumes this data, so don't fire server actions that would race the
  // auth handoff; queries enable automatically once the user navigates away.
  const isAuthHandoffPage =
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/onboarding';
  const canFetchUserData = isAuthenticated && !isAuthHandoffPage;
  const isListViewPage =
    pathname === '/lists' || pathname === '/contacts/lists';

  // Safely decode the JWT token with proper error handling
  let decoded: { permissions: string[]; roles?: string[] } = {
    permissions: [],
  };
  try {
    if (sessionToken && isAuthenticated) {
      decoded = jwtDecode(sessionToken);
    }
  } catch (error) {
    console.warn('Failed to decode session token:', error);
    decoded = { permissions: [] };
  }

  // The shell's identity, active teams and effective team preferences are one
  // client request and one shared query instance across PageLayout/children.
  const {
    data: bootstrap,
    isLoading: isUserDetailsLoading,
    isFetching: isUserBootstrapFetching,
    refetch: refetchUserBootstrap,
  } = useQuery({
    queryKey: ['userBootstrap'],
    queryFn: getUserBootstrap,
    enabled: canFetchUserData,
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const userDetails = bootstrap?.userDetails;
  const userTeams = bootstrap?.userTeams;
  const teamPreferences = bootstrap?.teamPreferences ?? null;
  const isLoadingUserTeams = isUserDetailsLoading;
  const isLoadingTeamPreferences = isUserDetailsLoading;
  const refetchUserDetails = refetchUserBootstrap;

  // Watchers are operational data, not list-shell data. Load them only on
  // watcher surfaces; mutation invalidations still refresh the shared key.
  const { data: watchersResponse, isLoading: isLoadingWatchers } = useQuery({
    queryKey: ['userWatchers'],
    queryFn: () => getUserWatchers(),
    enabled: canFetchUserData && pathname.startsWith('/watchers'),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Get the user's primary team (first team if multiple)
  const userTeam = useMemo(() => {
    return userTeams && userTeams.length > 0 ? userTeams[0] : null;
  }, [userTeams]);

  // Determine if user has a team (only after teams are loaded)
  const hasTeam = useMemo(() => {
    return !!(userTeams && userTeams.length > 0);
  }, [userTeams]);

  // Determine if user is the owner of their team
  const isTeamOwner = useMemo(() => {
    if (!userTeams || userTeams.length === 0 || !userDetails?.id) return false;
    return userTeam?.owner?.id === String(userDetails.id);
  }, [userTeams, userTeam, userDetails?.id]);

  // Invalidate all team-scoped caches when the user's team changes
  useEffect(() => {
    const currentTeamId = userTeam?.id ?? null;

    if (previousTeamIdRef.current === undefined) {
      previousTeamIdRef.current = currentTeamId;
      return;
    }

    if (previousTeamIdRef.current !== currentTeamId) {
      previousTeamIdRef.current = currentTeamId;
      queryClient.invalidateQueries({ queryKey: ['teamPreferences'] });
      queryClient.invalidateQueries({ queryKey: ['teamCredits'] });
      queryClient.invalidateQueries({ queryKey: ['totalBalance'] });
      queryClient.invalidateQueries({ queryKey: ['personalBalance'] });
      queryClient.invalidateQueries({ queryKey: ['companyLists'] });
      queryClient.invalidateQueries({ queryKey: ['contactLists'] });
      queryClient.invalidateQueries({ queryKey: ['bulkContactLists'] });
      queryClient.invalidateQueries({ queryKey: ['userCompanies'] });
      queryClient.invalidateQueries({
        queryKey: ['companies-all'],
        type: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['companies-recommended'],
        type: 'all',
      });
      queryClient.invalidateQueries({ queryKey: ['companies-unlocked'] });
      queryClient.invalidateQueries({ queryKey: ['companies-pipeline'] });
      queryClient.invalidateQueries({ queryKey: ['userWatchers'] });
    }
  }, [userTeam?.id, queryClient]);

  // Listen for real-time `team_plan_changed` events emitted by the backend
  // when the team owner upgrades/downgrades/cancels their plan. We invalidate
  // userDetails (so subscriptionPlanType updates), userTeams (so Team.maxSeats
  // and team plan update), and credits caches so the whole UI re-renders on
  // the new tier without waiting for the React Query stale window or a manual
  // refresh. Active for both the owner and every team member.
  const { on: socketOn } = useSocketContext();
  useEffect(() => {
    if (!isAuthenticated) return;
    const unsubscribe = socketOn('team_plan_changed', () => {
      queryClient.invalidateQueries({ queryKey: ['organization-workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['userBootstrap'] });
      queryClient.invalidateQueries({ queryKey: ['teamCredits'] });
      queryClient.invalidateQueries({ queryKey: ['totalBalance'] });
      queryClient.invalidateQueries({ queryKey: ['personalBalance'] });
    });
    return unsubscribe;
  }, [isAuthenticated, socketOn, queryClient]);

  // Credits/rewards do not gate list navigation. They stay lazy for /lists and
  // retain their normal cache/invalidation behavior everywhere else.
  const shouldLoadBalance = options?.loadBalance || !isListViewPage;
  const {
    data: personalBalance,
    isLoading: isPersonalBalanceLoading,
    isFetching: isPersonalBalanceFetching,
    refetch: refetchPersonalBalance,
  } = useQuery({
    queryKey: ['personalBalance'],
    queryFn: () => getPersonalBalanceOnly(),
    enabled:
      canFetchUserData && shouldLoadBalance && !isLoadingUserTeams && !hasTeam,
    placeholderData: undefined,
    staleTime: 10000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
  });

  // Team-credit consumers (navigation, exports and trial UI) share this one
  // query key. This replaces the total-balance endpoint's duplicate request
  // to the same team-credits endpoint.
  const {
    data: teamCredits,
    isLoading: isTeamCreditsLoading,
    refetch: refetchTeamCredits,
  } = useQuery<TeamCredits>({
    queryKey: TEAM_CREDITS_QUERY_KEY,
    queryFn: () =>
      sessionToken
        ? getTeamCredits(sessionToken)
        : Promise.resolve({ hasTeam: false }),
    enabled:
      canFetchUserData &&
      shouldLoadBalance &&
      !!sessionToken &&
      !isLoadingUserTeams &&
      hasTeam,
    staleTime: TEAM_CREDITS_STALE_TIME_MS,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const balance =
    hasTeam && teamCredits?.hasTeam
      ? teamCredits.availableCredits || 0
      : personalBalance || 0;
  const isBalanceLoading =
    isLoadingUserTeams ||
    (hasTeam ? isTeamCreditsLoading : isPersonalBalanceLoading);
  const refetchBalance = async () =>
    Promise.all([hasTeam ? refetchTeamCredits() : refetchPersonalBalance()]);

  // Note: Manual refresh is no longer needed since React Query cache invalidation handles updates

  // Determine effective products (team preferences take precedence)
  const effectiveProducts = useMemo(() => {
    if (userTeam?.organizationId) return teamPreferences?.products ?? [];
    if (!userDetails) return [];

    // Use team preferences if available, otherwise fall back to user products
    if (teamPreferences?.products && teamPreferences.products.length > 0) {
      return teamPreferences.products;
    }
    return userDetails.products || [];
  }, [
    userTeam?.organizationId,
    teamPreferences?.products,
    userDetails?.products,
  ]);

  // Determine effective departments (team preferences take precedence)
  const effectiveDepartments = useMemo(() => {
    if (userTeam?.organizationId)
      return teamPreferences?.departmentsSoldTo ?? [];
    if (!userDetails) return [];

    // Use team preferences if available, otherwise fall back to user departments
    if (
      teamPreferences?.departmentsSoldTo &&
      teamPreferences.departmentsSoldTo.length > 0
    ) {
      return teamPreferences.departmentsSoldTo;
    }
    return userDetails.departmentsSoldTo || [];
  }, [userTeam?.organizationId, teamPreferences, userDetails]);

  // Determine effective buying persona (team preferences take precedence)
  const effectiveBuyingPersona = useMemo(() => {
    if (userTeam?.organizationId) return teamPreferences?.buyingPersona ?? [];
    if (!userDetails) return [];

    // Use team preferences if available, otherwise fall back to user buying persona
    if (
      teamPreferences?.buyingPersona &&
      teamPreferences.buyingPersona.length > 0
    ) {
      return teamPreferences.buyingPersona;
    }
    return userDetails.buyingPersona || [];
  }, [
    userTeam?.organizationId,
    teamPreferences?.buyingPersona,
    userDetails?.buyingPersona,
  ]);

  // Determine effective company size (team preferences take precedence)
  const effectiveCompanySize = useMemo(() => {
    if (userTeam?.organizationId)
      return teamPreferences?.preferredCompanySize ?? [];
    if (!userDetails) return [];

    // Use team preferences if available, otherwise fall back to user company size
    if (
      teamPreferences?.preferredCompanySize &&
      teamPreferences.preferredCompanySize.length > 0
    ) {
      return teamPreferences.preferredCompanySize;
    }
    return userDetails.preferredCompanySize || [];
  }, [
    teamPreferences?.preferredCompanySize,
    userTeam?.organizationId,
    userDetails?.preferredCompanySize,
  ]);

  // Determine effective industries (team preferences take precedence)
  const effectiveIndustries = useMemo(() => {
    if (userTeam?.organizationId)
      return teamPreferences?.preferredIndustries ?? [];
    if (!userDetails) return [];

    // Use team preferences if available, otherwise fall back to user industries
    if (
      teamPreferences?.preferredIndustries &&
      teamPreferences.preferredIndustries.length > 0
    ) {
      return teamPreferences.preferredIndustries;
    }
    return userDetails.preferredIndustries || [];
  }, [
    userTeam?.organizationId,
    teamPreferences?.preferredIndustries,
    userDetails?.preferredIndustries,
  ]);

  // Determine effective deal-score event preferences (team preferences take precedence)
  const effectiveDealScoreEvents = useMemo(() => {
    if (userTeam?.organizationId)
      return teamPreferences?.dealScoreEventPreferences ?? null;
    if (
      teamPreferences?.dealScoreEventPreferences?.selected &&
      teamPreferences.dealScoreEventPreferences.selected.length > 0
    ) {
      return teamPreferences.dealScoreEventPreferences;
    }
    return userDetails?.dealScoreEventPreferences ?? null;
  }, [
    teamPreferences?.dealScoreEventPreferences,
    userTeam?.organizationId,
    userDetails?.dealScoreEventPreferences,
  ]);

  // Determine effective countries (team preferences take precedence)
  const effectiveCountries = useMemo(() => {
    if (userTeam?.organizationId)
      return teamPreferences?.preferredCountries ?? [];
    if (!userDetails) return [];

    // Use team preferences if available, otherwise fall back to user countries
    if (
      teamPreferences?.preferredCountries &&
      teamPreferences.preferredCountries.length > 0
    ) {
      return teamPreferences.preferredCountries;
    }
    return userDetails.preferredCountries || [];
  }, [
    userTeam?.organizationId,
    teamPreferences?.preferredCountries,
    userDetails?.preferredCountries,
  ]);

  // Ensure watchers is always a stable array
  const stableWatchers = useMemo(() => {
    return watchersResponse?.watchers || [];
  }, [watchersResponse?.watchers]);

  const isAdmin = useMemo(() => {
    return decoded?.permissions?.includes(Permissions.WRITE_ADMIN);
  }, [decoded?.permissions]);

  const isEAUser = useMemo(() => {
    return decoded?.permissions?.includes(Permissions.EA_USER);
  }, [decoded?.permissions]);

  const isSHV2User = useMemo(() => {
    return decoded?.permissions?.includes(Permissions.SH_V2);
  }, [decoded?.permissions]);

  const hasCustomSignalsAccess = useMemo(() => {
    return decoded?.permissions?.includes(Permissions.CUSTOM_SIGNALS);
  }, [decoded?.permissions]);

  const isWorkspaceAdmin = useMemo(() => {
    return decoded.roles?.includes('workspace_admin') ?? false;
  }, [decoded.roles]);

  return {
    userDetails: userDetails ?? null,
    isUserDetailsLoading,
    isUserBootstrapFetching,
    balance, // Total balance (personal + team)
    personalBalance, // Personal balance only
    isBalanceLoading,
    isPersonalBalanceLoading,
    isPersonalBalanceFetching, // Whether personal balance is currently being fetched
    refetchBalance,
    refetchPersonalBalance,
    refreshBalance: refetchBalance, // Backward compatibility alias
    refetchUserDetails,
    // Team data
    team: userTeam,
    userTeams: userTeams ?? [],
    hasTeam, // Boolean indicating if user has a team
    isTeamOwner, // Whether the current user is the owner of their team
    isLoadingUserTeams,
    // Watchers data
    watchers: stableWatchers,
    isLoadingWatchers,
    // Team preferences data
    teamPreferences,
    isLoadingTeamPreferences,
    // Effective data (team or user)
    effectiveProducts,
    effectiveDepartments,
    effectiveBuyingPersona,
    effectiveCompanySize,
    effectiveIndustries,
    effectiveCountries,
    effectiveDealScoreEvents,
    isAdmin,
    isEAUser,
    isSHV2User,
    hasCustomSignalsAccess,
    isWorkspaceAdmin,
  };
};
